import React, { useEffect, useState } from 'react';
import { supabase } from '../services/supabase';
import { ShieldCheck, AlertCircle, CreditCard, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const SubscriptionStatusBadge: React.FC = () => {
  const [status, setStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const fetchSubscription = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          if (isMounted) setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('subscriptions')
          .select('status, plan_type')
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          console.error("Erro ao buscar assinatura:", error);
        } else if (data && isMounted) {
          setStatus(data.status);
        } else if (isMounted) {
          setStatus('inactive');
        }
      } catch (err) {
        console.error("Erro geral ao buscar assinatura:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchSubscription();

    // Set up realtime subscription updates
    const channel = supabase
      .channel('subscription_badge_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions'
        },
        () => {
          // Re-fetch when there's an update
          fetchSubscription();
        }
      )
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(channel);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-2 animate-pulse">
        <div className="h-6 w-32 bg-raizama-border/30 rounded-full"></div>
      </div>
    );
  }

  if (status === 'active') {
    return (
      <div 
        onClick={() => navigate('/plans')}
        className="mb-4 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-xl p-3 flex items-center justify-between cursor-pointer hover:from-emerald-500/20 hover:to-teal-500/20 transition-all shadow-sm shadow-emerald-900/20"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/20 rounded-lg shrink-0">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-emerald-400 leading-tight">PRO Ativo</h3>
            <p className="text-xs text-emerald-500/70 font-medium">Acesso ilimitado liberado</p>
          </div>
        </div>
        <div className="text-xs font-bold text-emerald-500/50 bg-emerald-500/10 px-2 py-1 rounded-md uppercase tracking-wider">
          Gerenciar
        </div>
      </div>
    );
  }

  if (status === 'past_due' || status === 'unpaid' || status === 'incomplete') {
    return (
      <div 
        onClick={() => navigate('/plans')}
        className="mb-4 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center justify-between cursor-pointer hover:bg-amber-500/20 transition-all shadow-sm shadow-amber-900/20"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/20 rounded-lg shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-amber-400 leading-tight">Atenção com a Assinatura</h3>
            <p className="text-xs text-amber-500/70 font-medium">Pagamento pendente</p>
          </div>
        </div>
        <div className="text-xs font-bold text-amber-500/50 bg-amber-500/10 px-2 py-1 rounded-md uppercase tracking-wider">
          Resolver
        </div>
      </div>
    );
  }

  // Fallback for 'inactive', 'canceled', or null (Free plan)
  return (
    <div 
      onClick={() => navigate('/plans')}
      className="mb-4 bg-raizama-surface/50 border border-raizama-border/50 rounded-xl p-3 flex items-center justify-between cursor-pointer hover:border-raizama-action/50 transition-all"
    >
      <div className="flex items-center gap-3">
        <div className="p-2 bg-raizama-bg rounded-lg shrink-0 border border-raizama-border/30">
          <CreditCard className="w-5 h-5 text-raizama-textSec" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-raizama-textMain leading-tight">Plano Gratuito</h3>
          <p className="text-xs text-raizama-textSec font-medium">Faça upgrade para o PRO</p>
        </div>
      </div>
      <div className="text-xs font-bold text-raizama-action bg-raizama-action/10 px-2 py-1 rounded-md uppercase tracking-wider">
        Assinar
      </div>
    </div>
  );
};
